import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
    Building, Users, User, ChevronDown, ChevronRight, Search,
    Shield, Mail, RefreshCw, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InstitutionalManagement({ authToken }) {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedInstitutions, setExpandedInstitutions] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    const fetchInstitutionalData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${authToken}` };

            // Fetch all users and subscription codes (correct endpoint = /subscription/codes)
            const [usersRes, codesRes] = await Promise.all([
                axios.get(`${API}/admin/users`, { headers }),
                axios.get(`${API}/subscription/codes`, { headers }),
            ]);

            const users = usersRes.data || [];
            const codes = codesRes.data || [];

            // Index users by email + id for fast lookup
            const usersByEmail = {};
            const usersById = {};
            users.forEach(u => {
                if (u.email) usersByEmail[u.email.toLowerCase()] = u;
                if (u.id) usersById[u.id] = u;
            });

            // Build institution map from CSIC codes
            const institutionMap = {};
            codes.filter(c => c.code_type === 'CSIC').forEach(csic => {
                institutionMap[csic.code] = {
                    csic: csic.code,
                    name: csic.organization_name || csic.code,
                    status: csic.is_active ? 'active' : 'revoked',
                    created_at: csic.created_at,
                    users: [],
                    usic_codes: [],
                };
            });

            // Attach USICs and their users (via used_by_email or used_by user id)
            codes.filter(c => c.code_type === 'USIC').forEach(usic => {
                const parent = usic.parent_csic;
                if (!parent || !institutionMap[parent]) return;
                institutionMap[parent].usic_codes.push(usic);

                let user = null;
                if (usic.used_by_email) {
                    user = usersByEmail[usic.used_by_email.toLowerCase()] || null;
                }
                if (!user && usic.used_by) {
                    user = usersById[usic.used_by] || null;
                }
                if (user) {
                    // Avoid duplicates if a user somehow appears twice
                    if (!institutionMap[parent].users.some(u => u.id === user.id)) {
                        institutionMap[parent].users.push({ ...user, usic: usic.code });
                    }
                }
            });

            const institutionList = Object.values(institutionMap)
                .sort((a, b) => a.name.localeCompare(b.name));

            setInstitutions(institutionList);
        } catch (error) {
            console.error('Error fetching institutional data:', error);
            toast.error('Failed to load institutional data');
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchInstitutionalData();
    }, [fetchInstitutionalData]);

    const toggleInstitution = (csic) => {
        setExpandedInstitutions(prev => ({
            ...prev,
            [csic]: !prev[csic]
        }));
    };

    const getRoleBadge = (role) => {
        const roleColors = {
            'doctor': 'bg-blue-100 text-blue-700',
            'consultant': 'bg-purple-100 text-purple-700',
            'nhs_manager': 'bg-amber-100 text-amber-700',
            'institution_admin': 'bg-teal-100 text-teal-700',
            'super_admin': 'bg-rose-100 text-rose-700'
        };
        return roleColors[role] || 'bg-slate-100 text-slate-700';
    };

    const filteredInstitutions = institutions.filter(inst => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            inst.name.toLowerCase().includes(query) ||
            inst.csic.toLowerCase().includes(query) ||
            inst.users.some(u => 
                u.full_name?.toLowerCase().includes(query) ||
                u.email?.toLowerCase().includes(query)
            )
        );
    });

    const exportToCSV = () => {
        const rows = [['Institution', 'CSIC', 'User Name', 'Email', 'Role', 'USIC', 'Status']];
        
        filteredInstitutions.forEach(inst => {
            if (inst.users.length === 0) {
                rows.push([inst.name, inst.csic, '', '', '', '', inst.status]);
            } else {
                inst.users.forEach(user => {
                    rows.push([
                        inst.name,
                        inst.csic,
                        user.full_name,
                        user.email,
                        user.role,
                        user.usic || '',
                        inst.status
                    ]);
                });
            }
        });

        const csv = rows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `institutions_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('Exported to CSV');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
            </div>
        );
    }

    const totalUsers = institutions.reduce((sum, inst) => sum + inst.users.length, 0);
    const activeInstitutions = institutions.filter(inst => inst.status === 'active').length;

    return (
        <div className="space-y-6" data-testid="institutional-management">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <Card>
                    <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <Building className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">{institutions.length}</p>
                                <p className="text-xs sm:text-sm text-slate-500 truncate">Institutions</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">{totalUsers}</p>
                                <p className="text-xs sm:text-sm text-slate-500 truncate">Total Users</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">{activeInstitutions}</p>
                                <p className="text-xs sm:text-sm text-slate-500 truncate">Active</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search and Actions */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search institutions, users, or emails..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="institution-search"
                    />
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchInstitutionalData} className="flex-1 sm:flex-none">
                        <RefreshCw className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Refresh</span>
                    </Button>
                    <Button variant="outline" onClick={exportToCSV} className="flex-1 sm:flex-none">
                        <Download className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Export CSV</span>
                    </Button>
                </div>
            </div>

            {/* Institution List */}
            <div className="space-y-3">
                {filteredInstitutions.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Building className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                            <p className="text-slate-500">No institutions found</p>
                        </CardContent>
                    </Card>
                ) : (
                    filteredInstitutions.map(institution => (
                        <Card key={institution.csic} className="overflow-hidden" data-testid={`institution-card-${institution.csic}`}>
                            <div
                                className="flex items-center justify-between gap-2 p-3 sm:p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                                onClick={() => toggleInstitution(institution.csic)}
                            >
                                <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                                    {expandedInstitutions[institution.csic] ? (
                                        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                                    ) : (
                                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                                    )}
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                                        <Building className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-semibold text-slate-900 truncate text-sm sm:text-base">{institution.name}</h3>
                                        <p className="text-xs sm:text-sm text-slate-500 font-mono truncate">{institution.csic}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                                    <Badge className={institution.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                                        {institution.status}
                                    </Badge>
                                    <div className="text-right">
                                        <p className="font-semibold text-slate-900 text-sm sm:text-base">{institution.users.length}</p>
                                        <p className="text-[10px] sm:text-xs text-slate-500">users</p>
                                    </div>
                                </div>
                            </div>

                            {expandedInstitutions[institution.csic] && (
                                <div className="border-t border-slate-100 bg-slate-50/50">
                                    {institution.users.length === 0 ? (
                                        <div className="p-6 text-center text-slate-500">
                                            <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                                            <p>No users registered yet</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-slate-100">
                                            {institution.users.map(user => (
                                                <div key={user.id} className="flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4 hover:bg-white transition-colors">
                                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                                            <User className="w-4 h-4 text-slate-500" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="font-medium text-slate-900 truncate text-sm">{user.full_name}</p>
                                                            <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                                                                <Mail className="w-3 h-3 flex-shrink-0" />
                                                                <span className="truncate">{user.email}</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                                        {user.usic && (
                                                            <span className="text-[10px] sm:text-xs font-mono text-slate-400 hidden sm:inline">{user.usic}</span>
                                                        )}
                                                        <Badge className={`${getRoleBadge(user.role)} text-[10px] sm:text-xs`}>
                                                            {user.role?.replace('_', ' ')}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
}
